import {
    Component,
    computed,
    forwardRef,
    inject,
    InjectionToken,
    input,
    InputSignal,
    signal,
    CUSTOM_ELEMENTS_SCHEMA,
    output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IS3File } from '@shs-digi-care-monorepo/interfaces';
import { SnackbarService } from '../sh-snackbar/snackbar.service';
import { FileNameValidationOperations } from '../../util/file-name-validator.util';

/**
 * Injection token for the default `accept` attribute used by
 * {@link ShFileInputComponent}. Each application can provide its own value
 * (e.g. a tenant-specific allow-list of MIME types/extensions) at the root
 * provider level so individual components don't have to wire it up manually.
 */
export const FILE_INPUT_ACCEPTED_TYPES = new InjectionToken<string>('FILE_INPUT_ACCEPTED_TYPES', {
    providedIn: 'root',
    factory: () => '*/*'
});

/**
 * Status of a locally selected file in {@link ShFileInputComponent}. Use the
 * enum values instead of repeating string literals across the codebase.
 */
export const FileUploadStatus = {
    Pending: 'pending',
    Uploading: 'uploading',
    Success: 'success',
    Error: 'error'
} as const;
export type FileUploadStatus = (typeof FileUploadStatus)[keyof typeof FileUploadStatus];

export interface FileUploadItem {
    file: File;
    status: FileUploadStatus;
    errorMessage?: string;
}

/**
 * File input component with drag-and-drop support.
 * Features:
 * - File selection via drag-and-drop or browse button
 * - File removal
 * - External upload status control
 * - Max file size validation
 * - No modal required
 */
@Component({
    selector: 'sh-file-input',
    templateUrl: './sh-file-input.component.html',
    standalone: true,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ShFileInputComponent),
            multi: true
        }
    ],
    imports: [FormsModule, ReactiveFormsModule, CommonModule, TranslatePipe],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ShFileInputComponent implements ControlValueAccessor {
    // Inputs
    public disabled: InputSignal<boolean> = input<boolean>(false);
    public acceptedFileTypes: InputSignal<string> = input<string>(inject(FILE_INPUT_ACCEPTED_TYPES));
    public multiple: InputSignal<boolean> = input<boolean>(true);
    /** Pre-loaded server-side attachments to display alongside new uploads */
    public serverFiles: InputSignal<IS3File[]> = input<IS3File[]>([]);
    public fullHeight: InputSignal<boolean> = input(false);

    // Outputs
    public filesChanged = output<FileUploadItem[]>();
    /** Emits the S3 key when the user clicks download on a server file */
    public serverFileDownload = output<string>();
    /** Emits the S3 key when the user clicks remove on a server file */
    public serverFileDelete = output<string>();

    // Internal state
    public files: FileUploadItem[] = [];
    public isDragOver = false;

    private readonly snackbarService = inject(SnackbarService);
    private readonly translateService = inject(TranslateService);

    // ControlValueAccessor callbacks

    private onChange: (_: unknown) => void = () => {};

    private onTouched: () => void = () => {};

    constructor() {}

    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files) {
            this.addFiles(Array.from(input.files));
            input.value = ''; // Reset input to allow selecting the same file again
        }
    }

    public onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;

        if (this.isDisabledInternal()) {
            return;
        }

        const files = event.dataTransfer?.files;
        if (files) {
            this.addFiles(Array.from(files));
        }
    }

    public onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        if (!this.isDisabledInternal()) {
            this.isDragOver = true;
        }
    }

    public onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
    }

    private addFiles(newFiles: File[]): void {
        // Drag-and-drop bypasses the native input's accept attribute, so we must enforce it here.
        const accept = this.acceptedFileTypes();
        const acceptableFiles: File[] = [];
        let hadTypeRejection = false;
        for (const file of newFiles) {
            if (this.isAcceptedFileType(file, accept)) {
                acceptableFiles.push(file);
            } else {
                hadTypeRejection = true;
            }
        }
        if (hadTypeRejection) {
            this.snackbarService.warning(
                this.translateService.instant('common.file-upload.files-not-added-wrong-type')
            );
        }

        const { valid, errors } = FileNameValidationOperations.validateFiles(acceptableFiles);

        for (const error of errors) {
            const message = this.translateService.instant(error.reason, error.params);
            this.snackbarService.warning(message);
        }

        valid.forEach((file) => {
            const fileItem: FileUploadItem = {
                file,
                status: FileUploadStatus.Pending
            };

            if (!this.multiple() && this.files.length > 0) {
                this.files = [fileItem];
            } else {
                this.files.push(fileItem);
            }
        });

        this.emitChanges();
    }

    public removeFile(index: number): void {
        this.files.splice(index, 1);
        this.emitChanges();
        this.onTouched();
    }

    /**
     * Mirrors the native HTML `accept` attribute matching for drag-and-dropped files.
     * Accepts wildcards like `image/*`, concrete MIME types, and extensions like `.pdf`.
     */
    private isAcceptedFileType(file: File, accept: string): boolean {
        if (!accept || accept.trim() === '' || accept.trim() === '*/*') {
            return true;
        }
        const tokens = accept
            .split(',')
            .map((t) => t.trim().toLowerCase())
            .filter((t) => t.length > 0);
        if (tokens.length === 0) {
            return true;
        }
        const fileName = file.name.toLowerCase();
        const fileType = (file.type || '').toLowerCase();
        return tokens.some((token) => {
            if (token.startsWith('.')) {
                return fileName.endsWith(token);
            }
            if (token.endsWith('/*')) {
                const prefix = token.slice(0, -1); // keep trailing slash, e.g. "image/"
                return fileType.startsWith(prefix);
            }
            return fileType === token;
        });
    }

    public onRemoveClick(event: Event, index: number): void {
        event.preventDefault();
        if (!this.isDisabledInternal()) {
            this.removeFile(index);
        }
    }

    public updateFileStatus(index: number, status: FileUploadStatus, errorMessage?: string): void {
        if (index >= 0 && index < this.files.length) {
            this.files[index].status = status;
            if (errorMessage) {
                this.files[index].errorMessage = errorMessage;
            }
        }
    }

    public triggerFileInput(): void {
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) {
            fileInput.click();
        }
    }

    private emitChanges(): void {
        this.filesChanged.emit(this.files);
        this.onChange(this.files);
    }

    public getStatusIcon(status: string): string {
        switch (status) {
            case FileUploadStatus.Success:
                return 'success';
            case FileUploadStatus.Error:
                return 'error';
            case FileUploadStatus.Uploading:
                return ''; // Will use spinner
            case FileUploadStatus.Pending:
            default:
                return 'icon-wrapper scheduled';
        }
    }

    public onDownloadServerFile(event: Event, key: string): void {
        event.preventDefault();
        this.serverFileDownload.emit(key);
    }

    public onDeleteServerFile(event: Event, key: string): void {
        event.preventDefault();
        if (!this.isDisabledInternal()) {
            this.serverFileDelete.emit(key);
        }
    }

    public getServerFileName(key: string): string {
        return key.split('/').pop() ?? key;
    }

    public formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    }

    // ControlValueAccessor implementation
    public writeValue(value: FileUploadItem[]): void {
        if (value && Array.isArray(value)) {
            this.files = value;
        } else {
            this.files = [];
        }
    }

    public registerOnChange(fn: (_: unknown) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabledByCva.set(isDisabled);
    }

    private disabledByCva = signal(false);

    /**
     * Effective disabled state — true when either the `disabled` input is set
     * or the host FormControl was disabled via the ControlValueAccessor API.
     */
    public isDisabledInternal = computed(() => this.disabled() || this.disabledByCva());
}
